import TuneIcon from '@mui/icons-material/Tune'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { Box, Button, Stack } from '@mui/material'
import { GridColumnVisibilityModel, GridFilterModel } from '@mui/x-data-grid-premium'
import CustomFiltersModal from 'components/DataGrid/components/CustomFiltersModal'
import CustomFiltersMenu from 'components/ui/custom-filters-menu-button'
import React, { Dispatch, FC, RefObject, SetStateAction, useMemo, useState } from 'react'
import { defaultVisibilityModel, minimalistColumns, textOnlyColumns, websiteAccess } from './defaultModels'

interface TeamTasksCustomFilterProps {
  isCustomViewsOpen: boolean
  customView: string
  setCustomView: Dispatch<SetStateAction<string>>
  viewsAnchorEl: null | HTMLElement
  onOpenCustomViews: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  onClose: () => void
  onChangeVisibility: (newModel: GridColumnVisibilityModel, customView?: string) => void
  customViewsRef?: RefObject<HTMLDivElement>
  filterModel?: GridFilterModel
  setFilterModel?: Dispatch<SetStateAction<GridFilterModel>>
}

const TeamTasksCustomFilter: FC<TeamTasksCustomFilterProps> = ({
  isCustomViewsOpen,
  customView,
  setCustomView,
  viewsAnchorEl,
  onOpenCustomViews,
  onClose,
  onChangeVisibility,
  customViewsRef,
  filterModel,
  setFilterModel,
}) => {
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<null | HTMLElement>(null)
  const isFiltersOpen = Boolean(filtersAnchorEl)

  const handleOpenFilters = (event: React.MouseEvent<HTMLElement>) => {
    setFiltersAnchorEl(event.currentTarget)
  }

  const handleCloseFilters = () => {
    setFiltersAnchorEl(null)
  }

  const handleChangeView = (newModel: GridColumnVisibilityModel, customView: string) => {
    setCustomView(customView)
    onChangeVisibility(newModel)
  }

  // Check current postdated filter state
  const postdatedFilter = useMemo(() => {
    return filterModel?.items?.find(item => item.field === 'isPostdated')
  }, [filterModel])

  const isPostdatedFilterActive = postdatedFilter !== undefined
  const postdatedFilterValue = postdatedFilter?.value

  // Toggle postdated filter
  const handleTogglePostdated = (showPostdated: boolean | null) => {
    if (!setFilterModel) return

    setFilterModel(prev => {
      // Remove existing isPostdated filter
      const otherFilters = prev.items.filter(item => item.field !== 'isPostdated')

      // If null, just remove the filter (show all)
      if (showPostdated === null) {
        return { ...prev, items: otherFilters }
      }

      // Add new filter with the specified value
      const newFilter = {
        id: 'postdated-filter',
        field: 'isPostdated',
        operator: 'is',
        value: showPostdated,
      }

      return { ...prev, items: [...otherFilters, newFilter] }
    })
  }

  const isDefault = customView === 'default'
  const isMinimalist = customView === 'minimalist'
  const isText = customView === 'textOnly'
  const isWebsite = customView === 'websiteAccess'

  // Count active quick filters (non-default filters in the filters menu)
  const quickFilterCount = useMemo(() => {
    return isPostdatedFilterActive ? 1 : 0
  }, [isPostdatedFilterActive])

  return (
    <Box
      id="toolbar-custom-filter-menu"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <CustomFiltersMenu
        icon={<VisibilityIcon sx={{ fontSize: '30px' }} />}
        onOpenCustomFilters={onOpenCustomViews}
        count={customView && customView?.length > 0 ? 1 : 0}
        badgeColor={'icon.teamtasks'}
        helperRef={customViewsRef ?? null}
        tooltipTitle={'Custom Views'}
      />
      <CustomFiltersModal isOpen={isCustomViewsOpen} anchorEl={viewsAnchorEl} handleCloseCustomFilters={onClose}>
        <Stack gap={1} className="task-view-menu">
          <Button
            onClick={() => handleChangeView(defaultVisibilityModel, 'default')}
            sx={{ borderColor: isDefault ? 'text.highlight' : '' }}
            variant="outlined"
          >
            Default
          </Button>
          <Button
            onClick={() => handleChangeView(minimalistColumns, 'minimalist')}
            sx={{ borderColor: isMinimalist ? 'text.highlight' : '' }}
            variant="outlined"
          >
            Minimalist
          </Button>
          <Button
            onClick={() => handleChangeView(textOnlyColumns, 'textOnly')}
            sx={{ borderColor: isText ? 'text.highlight' : '' }}
            variant="outlined"
          >
            Text Only
          </Button>
          <Button
            onClick={() => handleChangeView(websiteAccess, 'websiteAccess')}
            sx={{ borderColor: isWebsite ? 'text.highlight' : '' }}
            variant="outlined"
          >
            Website Access
          </Button>
        </Stack>
      </CustomFiltersModal>
      <CustomFiltersMenu
        icon={<TuneIcon sx={{ fontSize: '30px' }} />}
        onOpenCustomFilters={handleOpenFilters}
        count={quickFilterCount}
        badgeColor={'icon.teamtasks'}
        helperRef={customViewsRef ?? null}
        tooltipTitle={'Quick Filters'}
      />
      <CustomFiltersModal
        isOpen={isFiltersOpen}
        anchorEl={filtersAnchorEl}
        handleCloseCustomFilters={handleCloseFilters}
      >
        <Stack gap={1} className="task-filter-menu">
          <Button
            onClick={() => handleTogglePostdated(false)}
            sx={{
              borderColor: isPostdatedFilterActive && postdatedFilterValue === false ? 'text.highlight' : '',
              backgroundColor: isPostdatedFilterActive && postdatedFilterValue === false ? 'action.selected' : '',
            }}
            variant="outlined"
          >
            Hide Postdated
          </Button>
          <Button
            onClick={() => handleTogglePostdated(true)}
            sx={{
              borderColor: isPostdatedFilterActive && postdatedFilterValue === true ? 'text.highlight' : '',
              backgroundColor: isPostdatedFilterActive && postdatedFilterValue === true ? 'action.selected' : '',
            }}
            variant="outlined"
          >
            Only Postdated
          </Button>
          <Button
            onClick={() => handleTogglePostdated(null)}
            sx={{
              borderColor: !isPostdatedFilterActive ? 'text.highlight' : '',
              backgroundColor: !isPostdatedFilterActive ? 'action.selected' : '',
            }}
            variant="outlined"
          >
            Show All
          </Button>
        </Stack>
      </CustomFiltersModal>
    </Box>
  )
}
export default TeamTasksCustomFilter
